/**
 * @file StageList.tsx
 * @description Список стадий воронки сделок (phase09; phase19.31 — по образцу
 * стадий заказов: строка 56 «⠿ · ● название / вид · N сделок · срок 24 ч»).
 *
 * Цвет стадии магазина — только точкой (SC28), кода стадии и подписи «SLA»
 * в строке нет. Нажатие на строку открывает правку, «Удалить» — в «⋯»
 * последним пунктом. Порядок меняется перетаскиванием за «⠿» (@dnd-kit,
 * паттерн из questionnaire) — только с правом настройки CRM.
 */

import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2 } from "lucide-react";

import { StageDot } from "../StageDot";
import { Badge } from "../../ui/badge";
import { LIST_FRAME_CLASS } from "../../ui/ListStates";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { STAGE_KIND_LABEL, type PipelineStageDto } from "../../../lib/api-crm";
import { stageSecondLine } from "../../../lib/crm-pipeline-view";
import { cn } from "@/lib/utils";

interface Props {
  stages: PipelineStageDto[];
  canConfig: boolean;
  onEdit: (stage: PipelineStageDto) => void;
  onDelete: (stage: PipelineStageDto) => void;
  onReorder: (activeId: string, overId: string) => void;
}

export function StageList({ stages, canConfig, onEdit, onDelete, onReorder }: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) onReorder(String(active.id), String(over.id));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
        <ul aria-label="Стадии воронки" className={cn(LIST_FRAME_CLASS, "divide-y divide-[var(--border)]")}>
          {stages.map((stage) => (
            <StageRow key={stage.id} stage={stage} canConfig={canConfig} onEdit={() => onEdit(stage)} onDelete={() => onDelete(stage)} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

interface RowProps {
  stage: PipelineStageDto;
  canConfig: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function StageRow({ stage, canConfig, onEdit, onDelete }: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stage.id, disabled: !canConfig });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const body = (
    <>
      <StageDot color={stage.color} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-2">
          <span className="type-body truncate font-medium text-on-surface">{stage.name}</span>
          {!stage.isActive ? <Badge variant="neutral">в архиве</Badge> : null}
        </span>
        <span className="type-caption truncate text-on-surface-variant">
          {STAGE_KIND_LABEL[stage.kind]} · {stageSecondLine(stage)}
        </span>
      </span>
    </>
  );

  return (
    <li ref={setNodeRef} style={style} className="flex min-h-14 items-stretch">
      {canConfig ? (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="flex w-10 shrink-0 cursor-grab touch-none items-center justify-center text-on-surface-variant hover:text-on-surface active:cursor-grabbing"
          aria-label={`Перетащить стадию «${stage.name}»`}
        >
          <GripVertical className="size-4" />
        </button>
      ) : null}
      {canConfig ? (
        <button
          type="button"
          onClick={onEdit}
          className="flex min-w-0 flex-1 items-center gap-3 py-2 pr-2 text-left outline-none transition-colors hover:bg-[var(--row-hover)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          {body}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2">{body}</div>
      )}
      {canConfig ? (
        <div className="flex shrink-0 items-center pr-2">
          <RowActionsMenu
            size="compact"
            title={`Стадия «${stage.name}»`}
            ariaLabel={`Действия со стадией «${stage.name}»`}
            actions={[
              { key: "edit", label: "Изменить", icon: Pencil, run: onEdit },
              { key: "delete", label: "Удалить", icon: Trash2, danger: true, run: onDelete },
            ]}
          />
        </div>
      ) : null}
    </li>
  );
}
